/**
 * 商圈版本：版本列表 + 按选中版本拉取 SURFACE 平铺数据；悬浮模式只拉官方默认商圈面。
 * 模块级导出供 useSurfaceLayer / index 在非 composable 上下文调用。
 */

import * as mapApi from '@src/api/map/api';
import { useStoreCity } from '@src/store/modules/city';
import { useStoreMapVersion } from '@src/store/modules/mapVersion';

// ---------------------------- 版本列表 ----------------------------

/** @type {Promise<void>|null} */
let versionListFetchPromise = null;

const normalizeCustomVersions = rawList =>
    (rawList ?? [])
        .filter(item => item.isRecycle === '0')
        .map(item => ({
            label: item.id,
            name: item.name,
            type: '1',
            isDefaultFlag: item.isDefaultFlag,
        }));

const normalizePublishedVersions = rawList =>
    (rawList ?? []).map(item => ({
        label: item.version,
        name: item.versionName,
        type: '2',
        isDefaultFlag: item.isDefaultFlag,
    }));

const fetchVersionList = async currentCity => {
    const mapId = currentCity.id ?? currentCity.mapId;
    const [customRes, publishedRes] = await Promise.all([
        mapApi.getMydrawMapList({ areaId: currentCity.areaId, mapId }).catch(() => ({ data: [] })),
        mapApi.getVersionAlreadyPublishVersion({ areaId: currentCity.areaId, mapId }).catch(() => ({ data: [] })),
    ]);

    return [
        ...normalizeCustomVersions(customRes?.data ?? customRes),
        ...normalizePublishedVersions(publishedRes?.data),
    ];
};

// ---------------------------- SURFACE 平铺数据 ----------------------------

/** @type {{ promise: Promise<{ icon: Array, polygon: object|null }>|null }} */
const surfaceTiledDataPromise = {
    promise: null,
};

const getCurrentCity = () => useStoreCity().currentCity;

/** 清空 SURFACE 平铺数据缓存（切换版本时调用） */
export const resetSurfaceTiledDataCache = () => {
    surfaceTiledDataPromise.promise = null;
};

const fetchDefaultSurfaceData = async currentCity => {
    const areaId = currentCity.areaId;
    const mapId = currentCity.id ?? currentCity.mapId;

    const [icon, areaPolygon] = await Promise.all([
        mapApi.getMydrawIconUseList({ surfaceType: 'SURFACE', areaId, mapId }).catch(() => ({ data: [] })),
        mapApi.getMapCitymapAreaPolygon({ map: currentCity, deep: 3 }).catch(() => ({ data: [] })),
    ]);

    return {
        icon: icon?.data ?? icon ?? [],
        polygon: areaPolygon,
    };
};

const fetchCustomMapSurfaceData = async (currentCity, drawMapId) => {
    const areaId = currentCity.areaId;
    const mapId = currentCity.id ?? currentCity.mapId;

    const [icon, areaPolygon] = await Promise.all([
        mapApi.getMydrawIconUseList({ surfaceType: 'SURFACE', areaId, mapId, drawMapId }).catch(() => ({ data: [] })),
        mapApi
            .getMydrawPlateListByCondition({
                surfaceType: 'SURFACE',
                areaId,
                customMapId: drawMapId,
            })
            .catch(() => ({ code: 0, data: [] })),
    ]);

    return {
        icon: icon?.data ?? icon ?? [],
        polygon: areaPolygon,
    };
};

const fetchPublishedVersionSurfaceData = async (currentCity, version) => {
    const previewRes = await mapApi
        .postVersionPreviewByVersion({
            areaId: currentCity.areaId,
            version,
            surfaceType: 'SURFACE',
            previewType: 'USER',
        })
        .catch(() => ({ data: {} }));

    return {
        icon: previewRes?.data?.ICON ?? [],
        polygon: { code: 0, data: previewRes?.data?.AREA_SURFACE ?? [] },
    };
};

const fetchSurfaceDataByVersion = async (currentCity, mapVersion) => {
    if (mapVersion.type === '1') {
        return fetchCustomMapSurfaceData(currentCity, mapVersion.label);
    }
    return fetchPublishedVersionSurfaceData(currentCity, mapVersion.label);
};

/** 按 mapVersion store 选中项拉取 SURFACE icon + polygon（单例 Promise 缓存） */
export const loadSurfaceTiledData = () => {
    if (!surfaceTiledDataPromise.promise) {
        surfaceTiledDataPromise.promise = (async () => {
            const currentCity = getCurrentCity();
            if (!currentCity?.areaId) {
                return { icon: [], polygon: null };
            }

            const storeMapVersion = useStoreMapVersion();
            const selectedVersion = storeMapVersion.mapVersion;

            if (!selectedVersion) {
                return fetchDefaultSurfaceData(currentCity);
            }

            const versionExists = storeMapVersion.versionList.some(
                item => item.label === selectedVersion.label && item.type === selectedVersion.type
            );
            if (!versionExists) {
                storeMapVersion.clearMapVersion();
                return fetchDefaultSurfaceData(currentCity);
            }

            return fetchSurfaceDataByVersion(currentCity, selectedVersion);
        })();
    }
    return surfaceTiledDataPromise.promise;
};

// ---------------------------- SURFACE 悬浮数据（始终官方默认，不跟版本） ----------------------------

/** @type {{ promise: Promise<{ polygon: object|null }>|null }} */
const surfaceHoverDataPromise = {
    promise: null,
};

/** 清空 SURFACE 悬浮数据缓存 */
export const resetSurfaceHoverDataCache = () => {
    surfaceHoverDataPromise.promise = null;
};

/** 拉取官方默认商圈面（deep: 3），不读 mapVersion；悬浮模式下商圈切换因此不生效 */
export const loadSurfaceHoverData = () => {
    if (!surfaceHoverDataPromise.promise) {
        surfaceHoverDataPromise.promise = (async () => {
            const currentCity = getCurrentCity();
            if (!currentCity?.areaId) {
                return { polygon: null };
            }

            const areaPolygon = await mapApi.getMapCitymapAreaPolygon({ map: currentCity, deep: 3 }).catch(() => ({ data: [] }));
            return { polygon: areaPolygon };
        })();
    }
    return surfaceHoverDataPromise.promise;
};

// ---------------------------- 重置 ----------------------------

/** 登出时重置版本列表缓存、平铺 / 悬浮数据缓存与 store 状态 */
export const resetMapVersionModule = () => {
    versionListFetchPromise = null;
    resetSurfaceTiledDataCache();
    resetSurfaceHoverDataCache();
    useStoreMapVersion().resetVersionState();
};

// ---------------------------- composable ----------------------------

export function useMapVersion() {
    const storeCity = useStoreCity();
    const storeMapVersion = useStoreMapVersion();

    /** 拉取版本列表并写入 store（同会话仅请求一次） */
    const init = async () => {
        if (storeMapVersion.isVersionListReady) return;

        if (!versionListFetchPromise) {
            versionListFetchPromise = (async () => {
                const currentCity = storeCity.currentCity;
                if (!currentCity?.areaId) {
                    storeMapVersion.setVersionList([]);
                    return;
                }

                const list = await fetchVersionList(currentCity);
                storeMapVersion.setVersionList(list);
            })();
        }

        try {
            await versionListFetchPromise;
        } catch (error) {
            versionListFetchPromise = null;
            storeMapVersion.setVersionList([]);
            throw error;
        }
    };

    return { init };
}
